import {
    Caption1,
    Card,
    CardHeader,
    shorthands,
    Text,
    makeStyles,
    cardHeaderClassNames,
    tokens,
    useId,
    Tooltip,
    mergeClasses
} from '@fluentui/react-components';
import { stringToBrightColor } from '../../helpers/string-to-bright-color';
import { KeyboardEventHandler } from 'react';
import { CheckboxUnchecked20Regular, CheckboxChecked20Regular } from '@fluentui/react-icons';
// import styles from './knowledge-base-selector.module.scss';

const useStyles = makeStyles({
    card: {
        width: '100%',
        height: "fit-content",
        position: 'relative',

        [`& > .${cardHeaderClassNames.root}`]: {
            flexShrink: 1,
        },
    },

    selectableContent: {
        width: '100%',
        minWidth: '0px',
        cursor: 'pointer',
    },

    active: {
        backgroundColor: '#dee5ff',
    },

    header: {
        flexShrink: 1,
        minWidth: '0px',

        [`& > .${cardHeaderClassNames.description}`]: {
            ...shorthands.overflow("hidden"),
            minWidth: '0px',
        },
        [`& > .${cardHeaderClassNames.header}`]: {
            flexShrink: 1,
        },
    },

    title: {
        width: "90%",
    },

    preview: {
        flexShrink: 0,
        maxWidth: "64px",
        maxHeight: "64px"
    },

    caption: {
        color: tokens.colorNeutralForeground3,
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ...shorthands.overflow("hidden"),
    },

    publishedStatus: {
        position: 'absolute',
        top: '8px',
        right: '8px',
        display: 'flex',
        alignItems: 'center',
    },

    publishedIcon: {
        flexShrink: 0,
    },

    publishedIconUnchecked: {
        flexShrink: 0,
        color: 'grey',
    },

    cardFooter: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        ...shorthands.gap("4px"),
    },
    cardFooterItem: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        justifyContent: "space-between",
        ...shorthands.gap("4px"),
    },
});

type KnowledgeBaseCardSelectableProps = {
    id: string;
    title: string;
    subtitle: string;
    selected: boolean;
    onClick?: () => void;
    documentCount: number;
    updatedAt: string;
    isFooterVisible: boolean;
    published: boolean;
    citation_view: boolean;
    canCreateKnowledgeBase: boolean;
    canCreateQuestionExamples: boolean;
}

export const KnowledgeBaseCardSelectable = ({
    id,
    title,
    subtitle,
    selected,
    onClick,
    documentCount,
    updatedAt,
    isFooterVisible,
    published,
    citation_view,
    canCreateKnowledgeBase,
    canCreateQuestionExamples
}: KnowledgeBaseCardSelectableProps) => {
    const labelId = useId('knowledge-base-card-label');
    const descriptionId = useId('knowledge-base-card-description');
    const publishedStatusId = useId('knowledge-base-published-status');
    const footerDetailsId = useId('knowledge-base-card-footer-details');
    const styles = useStyles();
    const canViewPublishedStatus = canCreateKnowledgeBase || canCreateQuestionExamples;

    const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick?.();
        }
    };

    return (
        <Card
            appearance='filled-alternative'
            className={mergeClasses(styles.card, selected && styles.active)}
        >
            <div
                aria-current={selected ? "page" : undefined}
                // Include card details in the name so browse navigation announces them too.
                aria-labelledby={[
                    labelId,
                    descriptionId,
                    canViewPublishedStatus ? publishedStatusId : undefined,
                    isFooterVisible ? footerDetailsId : undefined,
                ].filter(Boolean).join(' ')}
                className={styles.selectableContent}
                onClick={onClick}
                onKeyDown={handleKeyDown}
                role={'button'}
                tabIndex={0}
            >
                <CardHeader
                    image={
                        <img
                            src={
                                `https://ui-avatars.com/api?name=${title}&background=${stringToBrightColor(id)}`
                            }
                            alt={`${title} knowledge base logo`}
                            height={48}
                            width={48}
                        />
                    }
                    className={styles.header}
                    header={<Text id={labelId} weight="semibold" className={styles.title}>{title}</Text>}
                    description={
                        <Caption1 id={descriptionId} className={styles.caption}>{subtitle}</Caption1>
                    }
                />
                {canViewPublishedStatus && (
                    <span id={publishedStatusId} className="sr-only">
                        {published ? "Published knowledge base." : "Unpublished knowledge base."}
                    </span>
                )}
                {isFooterVisible && (
                    <div id={footerDetailsId} className={styles.cardFooter}>
                        <div className={styles.cardFooterItem}>
                            <span>Pages Indexed</span>
                            <span>{documentCount}</span>
                        </div>

                        <div className={styles.cardFooterItem}>
                            <span>Last Updated</span>
                            <span>{new Date(updatedAt).toLocaleDateString()}</span>
                        </div>
                    </div>
                )}
            </div>
            {canViewPublishedStatus && (
                <div
                    className={styles.publishedStatus}
                    aria-hidden="true"
                >
                    {published ? (
                        <Tooltip content="Published" positioning={"after-top"} relationship="label">
                            <CheckboxChecked20Regular className={styles.publishedIcon} />
                        </Tooltip>
                    ) : (
                        <Tooltip content="Unpublished" positioning={"after-top"} relationship="label">
                            <CheckboxUnchecked20Regular className={styles.publishedIconUnchecked} />
                        </Tooltip>
                    )}
                </div>
            )}
        </Card >
    )
}
